import {
  MotorFunnelState,
  MotorStepType,
  MotorFlowType,
  Driver,
  MotorFormAnswer,
  MotorFlowDefinition,
} from 'scenes/motor/types';
import {
  getAdditionalDrivers,
  getMainDriver,
  hasAddedDriverClaimsMade,
} from 'scenes/motor/selectors';

interface HasIndex {
  index: number;
}

export const insertDriver = (
  array: Driver[],
  item: Partial<Driver> & HasIndex
) => {
  const copy = [...array];
  copy[item.index] = item as Driver;
  return copy;
};

// Helper function, since adding an additional driver can happen from multiple places
const addAdditionalDriver = (
  funnelState: MotorFunnelState
): Partial<MotorFunnelState> => ({
  step: MotorStepType.AddDriver,
  flow: MotorFlowType.QuoteDetails,
  [MotorFlowType.QuoteDetails]: {
    ...funnelState[MotorFlowType.QuoteDetails],
    additionalDrivers: insertDriver(
      funnelState[MotorFlowType.QuoteDetails].additionalDrivers,
      funnelState[MotorFlowType.AddDriver]
    ),
  },
});

export const forwardLinks: MotorFlowDefinition = {
  [MotorStepType.GenderAndDateOfBirth]: { step: MotorStepType.YearsDriving },
  [MotorStepType.YearsDriving]: { step: MotorStepType.ClaimsMade },
  [MotorStepType.ClaimsMade]: (funnelState: MotorFunnelState) =>
    hasAddedDriverClaimsMade(funnelState)
      ? { step: MotorStepType.PayableClaims } // Display Payable Claims only when some claims have been made
      : addAdditionalDriver(funnelState), // Otherwise add the driver
  [MotorStepType.PayableClaims]: (funnelState: MotorFunnelState) =>
    addAdditionalDriver(funnelState),
};

export const backwardLinks: MotorFlowDefinition = {
  [MotorStepType.GenderAndDateOfBirth]: {
    step: MotorStepType.AddDriver,
    flow: MotorFlowType.QuoteDetails,
  },
  [MotorStepType.YearsDriving]: { step: MotorStepType.GenderAndDateOfBirth },
  [MotorStepType.ClaimsMade]: { step: MotorStepType.YearsDriving },
  [MotorStepType.PayableClaims]: { step: MotorStepType.ClaimsMade },
};

export const removeDriver = (
  funnelState: MotorFunnelState,
  action: MotorFormAnswer
) => ({
  [MotorFlowType.QuoteDetails]: {
    ...funnelState[MotorFlowType.QuoteDetails],
    additionalDrivers: getAdditionalDrivers(funnelState).filter(
      (item, index) => index !== action.formValues.driverIndex
    ),
  },
});

export const editDriver = (
  funnelState: MotorFunnelState,
  action: MotorFormAnswer
) => {
  const driverIndex = action.formValues.driverIndex ?? -1;
  return {
    step: action.targetStep ?? MotorStepType.Name,
    flow: MotorFlowType.AddDriver,
    [MotorFlowType.AddDriver]: {
      index: driverIndex,
      ...(driverIndex >= 0
        ? getAdditionalDrivers(funnelState)[driverIndex]
        : getMainDriver(funnelState)),
    },
  };
};
